import React, { useState, useEffect, useMemo } from 'react'
import { Copy, Check, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp, Zap, Target, BarChart3, Clock, Users } from 'lucide-react'
import { StatComparison, TopPerformersStack, GameResultsSummary } from './InteractiveCards'
import TeamLogo from './TeamLogo'
import RichFragmentRenderer from './RichFragments'

// Thinking indicator - simple pulsing dot
function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 text-gray-400 text-sm">
      <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
    </div>
  )
}

// Question type badge icons and colors
const QUESTION_TYPE_CONFIG = {
  live_score: { icon: Zap, label: 'Live', color: 'text-red-500', bg: 'bg-red-50' },
  recap: { icon: Clock, label: 'Recap', color: 'text-blue-500', bg: 'bg-blue-50' },
  betting: { icon: Target, label: 'Betting', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  player_stats: { icon: Users, label: 'Player', color: 'text-purple-500', bg: 'bg-purple-50' },
  analysis: { icon: BarChart3, label: 'Analysis', color: 'text-amber-500', bg: 'bg-amber-50' },
  prediction: { icon: Target, label: 'Prediction', color: 'text-pink-500', bg: 'bg-pink-50' },
  general: { icon: Zap, label: 'General', color: 'text-gray-500', bg: 'bg-gray-50' },
}

// Skeleton loader for messages
function MessageSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-16 h-4 bg-gray-200 rounded" />
        <div className="w-12 h-4 bg-gray-100 rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-5/6" />
        <div className="h-4 bg-gray-200 rounded w-4/6" />
      </div>
      <div className="flex gap-2 pt-2">
        <div className="w-20 h-6 bg-gray-100 rounded-full" />
        <div className="w-24 h-6 bg-gray-100 rounded-full" />
      </div>
    </div>
  )
}

// Unified quality badge - combines question type + confidence in one clean pill
function QualityBadge({ questionType, confidence, contextQuality }) {
  const config = QUESTION_TYPE_CONFIG[questionType] || QUESTION_TYPE_CONFIG.general
  const Icon = config.icon
  
  // Use context quality if available, otherwise confidence
  const score = contextQuality?.overall || confidence || 0
  const scoreColor = score >= 70 ? 'text-emerald-500' : score >= 40 ? 'text-amber-500' : 'text-gray-400'
  
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
        <Icon size={9} />
        <span className="font-medium">{config.label}</span>
      </span>
      {score > 0 && (
        <span className={`font-medium ${scoreColor}`}>{score}%</span>
      )}
    </div>
  )
}

// Team logos header for game context
function GameContextHeader({ teams, score }) {
  if (!teams || teams.length < 2) return null
  
  return (
    <div className="flex items-center justify-center gap-4 py-2 mb-3 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <TeamLogo code={teams[0]?.code} name={teams[0]?.name} size={24} />
        <span className="text-sm font-semibold text-gray-700">{teams[0]?.code}</span>
        {score && <span className="text-sm font-bold text-gray-900">{score[0]}</span>}
      </div>
      <span className="text-xs text-gray-400">vs</span>
      <div className="flex items-center gap-2">
        {score && <span className="text-sm font-bold text-gray-900">{score[1]}</span>}
        <span className="text-sm font-semibold text-gray-700">{teams[1]?.code}</span>
        <TeamLogo code={teams[1]?.code} name={teams[1]?.name} size={24} />
      </div>
    </div>
  )
}

// Simple markdown parser for AI responses
function parseMarkdown(text) {
  if (!text) return []
  
  const lines = text.split('\n')
  const elements = []
  let currentList = null
  let currentTable = null
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim()
    
    // Headers
    if (trimmed.startsWith('### ')) {
      if (currentList) { elements.push(currentList); currentList = null }
      elements.push({ type: 'h3', content: trimmed.slice(4) })
      return
    }
    if (trimmed.startsWith('## ')) {
      if (currentList) { elements.push(currentList); currentList = null }
      elements.push({ type: 'h2', content: trimmed.slice(3) })
      return
    }
    if (trimmed.startsWith('# ')) {
      if (currentList) { elements.push(currentList); currentList = null }
      elements.push({ type: 'h1', content: trimmed.slice(2) })
      return
    }
    
    // Bullet points
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      if (!currentList || currentList.type !== 'ul') {
        if (currentList) elements.push(currentList)
        currentList = { type: 'ul', items: [] }
      }
      currentList.items.push(trimmed.slice(2))
      return
    }
    
    // Numbered lists
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/)
    if (numberedMatch) {
      if (!currentList || currentList.type !== 'ol') {
        if (currentList) elements.push(currentList)
        currentList = { type: 'ol', items: [] }
      }
      currentList.items.push(numberedMatch[2])
      return
    }
    
    // Table rows
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (!currentTable) {
        currentTable = { type: 'table', rows: [] }
      }
      // Skip separator rows
      if (!trimmed.match(/^\|[\s-:|]+\|$/)) {
        const cells = trimmed.slice(1, -1).split('|').map(c => c.trim())
        currentTable.rows.push(cells)
      }
      return
    } else if (currentTable) {
      elements.push(currentTable)
      currentTable = null
    }
    
    // Empty line - close list
    if (trimmed === '') {
      if (currentList) { elements.push(currentList); currentList = null }
      return
    }
    
    // Regular paragraph
    if (currentList) { elements.push(currentList); currentList = null }
    elements.push({ type: 'p', content: trimmed })
  })
  
  // Close any open list/table
  if (currentList) elements.push(currentList)
  if (currentTable) elements.push(currentTable)
  
  return elements
}

// Render inline formatting (bold, italic, code)
function renderInline(text) {
  if (!text) return text
  
  // Bold **text** - proper bold, black text
  let result = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
  // Italic *text*
  result = result.replace(/\*([^*]+)\*/g, '<em class="text-gray-700">$1</em>')
  // Code `text`
  result = result.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-[13px] font-mono">$1</code>')
  
  return <span dangerouslySetInnerHTML={{ __html: result }} />
}

// Markdown renderer component - clean but readable
function MarkdownContent({ text }) {
  const elements = useMemo(() => parseMarkdown(text), [text])
  
  return (
    <div className="space-y-3">
      {elements.map((el, idx) => {
        switch (el.type) {
          case 'h1':
            return (
              <h1 key={idx} className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                {renderInline(el.content)}
              </h1>
            )
          case 'h2':
            return (
              <h2 key={idx} className="text-base font-semibold text-gray-900 mt-3 mb-1.5">
                {renderInline(el.content)}
              </h2>
            )
          case 'h3':
            return (
              <h3 key={idx} className="text-[15px] font-medium text-gray-800 mt-2 mb-1">
                {renderInline(el.content)}
              </h3>
            )
          case 'ul':
            return (
              <ul key={idx} className="space-y-1.5 my-2">
                {el.items.map((item, i) => (
                  <li key={i} className="flex gap-2 items-start text-[14px] leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                    <span className="text-gray-700">{renderInline(item)}</span>
                  </li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={idx} className="space-y-1.5 my-2">
                {el.items.map((item, i) => (
                  <li key={i} className="flex gap-2 items-start text-[14px] leading-relaxed">
                    <span className="text-gray-500 text-sm font-medium min-w-[18px]">
                      {i + 1}.
                    </span>
                    <span className="text-gray-700">{renderInline(item)}</span>
                  </li>
                ))}
              </ol>
            )
          case 'table':
            // Check if this is a game log table (has Date column + stats)
            const headers = el.rows[0]?.map(h => h.toLowerCase().trim()) || []
            const isGameLogTable = headers.some(h => ['date', 'game'].includes(h)) && 
                                   headers.some(h => ['pts', 'points', 'min'].includes(h))
            
            // Clean table styling - X axis (games/dates down), Y axis (stats across)
            return (
              <div key={idx} className="overflow-x-auto my-4 rounded-xl border border-gray-100 shadow-sm bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/80">
                      {el.rows[0]?.map((cell, i) => (
                        <th 
                          key={i} 
                          className={`py-2.5 px-3 font-semibold text-gray-500 text-[10px] uppercase tracking-wide ${
                            i === 0 ? 'text-left' : 'text-center'
                          }`}
                        >
                          {renderInline(cell)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {el.rows.slice(1).map((row, i) => {
                      return (
                        <tr 
                          key={i} 
                          className={`border-t border-gray-100 ${isGameLogTable ? 'hover:bg-gray-50' : ''}`}
                        >
                          {row.map((cell, j) => {
                            const cellStr = cell?.trim() || ''
                            
                            // Check if this is a stat/line format like "28/18" or "🟢 28/18"
                            const statLineMatch = cellStr.match(/^([🟢🔴]\s*)?(\d+)\/(\d+\.?\d*)/)
                            let isHit = null
                            let displayVal = cellStr
                            
                            if (statLineMatch) {
                              const actual = parseInt(statLineMatch[2])
                              const line = parseFloat(statLineMatch[3])
                              isHit = actual >= line
                              displayVal = `${actual}/${line % 1 === 0.5 ? line.toFixed(1) : Math.round(line)}`
                            }
                            
                            // Check if this looks like a score/number
                            const isNumeric = /^\d+%?$/.test(cellStr)
                            
                            return (
                              <td 
                                key={j} 
                                className={`py-2.5 px-3 text-[12px] ${
                                  j === 0 
                                    ? 'text-left font-medium text-gray-800' 
                                    : isHit === true
                                      ? 'text-center font-semibold text-green-600'
                                      : isHit === false
                                        ? 'text-center font-semibold text-red-500'
                                        : isNumeric 
                                          ? 'text-center tabular-nums text-gray-700' 
                                          : 'text-center text-gray-600'
                                }`}
                              >
                                {renderInline(displayVal)}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          case 'p':
          default:
            return <p key={idx} className="text-[14px] text-gray-700 leading-relaxed">{renderInline(el.content)}</p>
        }
      })}
    </div>
  )
}

// Typewriter with markdown - renders as it types
function TypewriterMarkdown({ text, speed = 5 }) {
  const [displayedText, setDisplayedText] = useState('')
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    setDisplayedText('')
    setIsComplete(false)
    let index = 0
    
    // Faster typing - 5ms per char
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1))
        index++
      } else {
        setIsComplete(true)
        clearInterval(timer)
      }
    }, speed)

    return () => clearInterval(timer)
  }, [text, speed])

  return (
    <div className="relative">
      <MarkdownContent text={displayedText} />
      {!isComplete && (
        <span 
          className="inline-block w-[2px] h-[18px] ml-0.5 animate-pulse absolute"
          style={{ backgroundColor: 'var(--accent-1)', verticalAlign: 'text-bottom' }}
        />
      )}
    </div>
  )
}

// Live thinking display - shows reasoning as it happens (like Grok)
function ThinkingSteps({ steps, isLive = false }) {
  const [expanded, setExpanded] = useState(isLive) // Auto-expand if live
  if (!steps || steps.length === 0) return null
  
  return (
    <div className="mb-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-gray-400 hover:text-gray-600 transition mb-2"
        style={{ fontStyle: 'italic' }}
      >
        {expanded ? 'Hide reasoning' : 'Show reasoning'}
      </button>
      {expanded && (
        <div 
          className="pl-3 border-l-2 animate-fade-in space-y-1"
          style={{ borderColor: 'var(--accent-1)', borderLeftWidth: '2px' }}
        >
          {steps.map((step, i) => (
            <div 
              key={i}
              className="flex items-start gap-2"
            >
              <span className="text-pink-500 text-xs mt-0.5">→</span>
              <p 
                style={{ 
                  fontSize: '13px',
                  color: '#6B7280',
                  fontStyle: 'italic',
                  lineHeight: '1.4'
                }}
              >
                {step}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Action button - refined
function ActionButton({ icon: Icon, onClick, active, title }) {
  return (
    <button
      onClick={onClick}
      className="p-2 rounded-lg transition-all duration-150 hover:bg-gray-50 active:scale-95"
      style={{ 
        opacity: active ? 1 : 0.3,
        color: active ? '#10B981' : '#6B7280'
      }}
      title={title}
    >
      <Icon size={14} strokeWidth={2} />
    </button>
  )
}

// Removed - merged into QualityBadge

export default function MessageList({ messages, contextData, onChipClick }) {
  const [copiedId, setCopiedId] = useState(null)
  const [hoveredMsg, setHoveredMsg] = useState(null)

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleChipClick = (chip) => {
    if (onChipClick) {
      onChipClick(chip)
    }
  }

  return (
    <div className="w-full py-6">
      {messages.map((msg, idx) => {
        const isLastAiMessage = msg.sender === 'ai' && idx === messages.length - 1
        const isHovered = hoveredMsg === msg.id
        
        return (
          <div 
            key={msg.id} 
            className="mb-6 transition-all duration-300 ease-out"
            style={{
              animation: 'fadeSlideIn 0.3s ease-out forwards',
              opacity: 0,
              transform: 'translateY(8px)',
            }}
            onMouseEnter={() => setHoveredMsg(msg.id)}
            onMouseLeave={() => setHoveredMsg(null)}
          >
            {/* User message - pill, right-aligned */}
            {msg.sender === 'user' && (
              <div className="flex justify-end mb-4">
                <div 
                  className="chat-message px-4 py-2.5 rounded-[20px] max-w-[85%] transition-transform duration-200 hover:scale-[1.01]"
                  style={{ 
                    backgroundColor: '#F3F4F6',
                    color: '#1F2937',
                    fontSize: '14px',
                    fontWeight: '450',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                >
                  {msg.text}
                </div>
              </div>
            )}
            
            {/* AI message - clean canvas, no agent badge */}
            {msg.sender === 'ai' && (
              <div className="relative group">

                {/* Thinking steps - show BEFORE answer like Grok */}
                {msg.thinking && msg.thinking.length > 0 && (
                  <div className="mb-3">
                    <ThinkingSteps steps={msg.thinking} isLive={isLastAiMessage} />
                  </div>
                )}

                {/* Auto-match banner - when agent derived game from player's team, not selected tab */}
                {msg.autoMatchedPlayer && (
                  <div 
                    className="mb-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2"
                    style={{ 
                      backgroundColor: 'rgba(0, 217, 255, 0.08)', 
                      border: '1px solid rgba(0, 217, 255, 0.2)',
                      color: '#0891b2'
                    }}
                  >
                    <span className="text-sm">🎯</span>
                    <span>
                      <strong>Auto-locked to {msg.autoMatchedPlayer}'s game</strong>
                      <span className="opacity-70"> — Stats panel may show a different matchup</span>
                    </span>
                  </div>
                )}
                {!msg.autoMatchedPlayer && msg.autoMatchedTeams && msg.autoMatchedTeams.length > 0 && (
                  <div 
                    className="mb-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2"
                    style={{ 
                      backgroundColor: 'rgba(0, 217, 255, 0.08)', 
                      border: '1px solid rgba(0, 217, 255, 0.2)',
                      color: '#0891b2'
                    }}
                  >
                    <span className="text-sm">📌</span>
                    <span>
                      <strong>Auto-locked {msg.autoMatchedTeams.join(' vs ')}</strong>
                      <span className="opacity-70"> — Pulled the recap based on your question</span>
                    </span>
                  </div>
                )}

                {/* Rich fragments - scorecards, top performers, props results */}
                {msg.richFragments && msg.richFragments.length > 0 && (
                  <div className="mb-3">
                    <RichFragmentRenderer fragments={msg.richFragments} />
                  </div>
                )}

                {/* AI response text - with markdown rendering */}
                <div className="chat-message prose prose-sm max-w-none">
                  {msg.isStreaming ? (
                    // Streaming: show thinking steps before first tokens, then render text
                    <div className="relative">
                      {!msg.text || msg.text.length === 0 ? (
                        <ThinkingIndicator />
                      ) : (
                        <>
                          <MarkdownContent text={msg.text} />
                          <span 
                            className="inline-block w-[2px] h-[14px] ml-0.5 animate-pulse"
                            style={{ backgroundColor: 'var(--accent-1, #FF4D6D)' }}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    // Complete: just render the markdown
                    <MarkdownContent text={msg.text} />
                  )}
                </div>
                
                {/* Subtle freshness + quality indicator - only show when we have meaningful data */}
                {!msg.isStreaming && (msg.dataAgeSeconds > 0 || (msg.contextQuality?.overall != null && msg.contextQuality.overall < 50)) && (
                  <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500">
                    {/* Data freshness indicator */}
                    {msg.dataAgeSeconds > 0 && (
                      <div className="flex items-center gap-1">
                        <span 
                          className={`w-1.5 h-1.5 rounded-full ${
                            msg.dataAgeSeconds < 60 ? 'bg-emerald-500' : 
                            msg.dataAgeSeconds < 180 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        />
                        <span>
                          {msg.dataAgeSeconds < 60 ? 'Live' : 
                           msg.dataAgeSeconds < 180 ? `${Math.round(msg.dataAgeSeconds / 60)}m ago` :
                           `${Math.round(msg.dataAgeSeconds / 60)}m old`}
                        </span>
                      </div>
                    )}
                    {/* Context quality - only show if below threshold */}
                    {msg.contextQuality?.overall != null && msg.contextQuality.overall < 50 && (
                      <>
                        {msg.dataAgeSeconds > 0 && <span className="text-gray-300 dark:text-gray-600">•</span>}
                        <span className="text-amber-500 dark:text-amber-400">limited data</span>
                      </>
                    )}
                  </div>
                )}

                {/* Suggested Actions - from API when context is missing */}
                {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {msg.suggestedActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => handleChipClick(action)}
                        className="px-3 py-1.5 text-xs font-medium rounded-full transition-all hover:scale-105 active:scale-95"
                        style={{
                          backgroundColor: 'rgba(255, 77, 109, 0.08)',
                          color: '#FF4D6D',
                          border: '1px solid rgba(255, 77, 109, 0.2)',
                        }}
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                )}

                {/* Action buttons - under message, only show when done streaming */}
                {!msg.isStreaming && (
                  <div 
                    className="mt-3 flex gap-1 transition-opacity duration-200"
                    style={{ opacity: isHovered ? 1 : 0.4 }}
                  >
                    <ActionButton 
                      icon={copiedId === msg.id ? Check : Copy}
                      onClick={() => handleCopy(msg.text, msg.id)}
                      active={copiedId === msg.id}
                      title={copiedId === msg.id ? 'Copied' : 'Copy'}
                    />
                    <ActionButton 
                      icon={ThumbsUp}
                      onClick={() => {}}
                      active={false}
                      title="Good response"
                    />
                    <ActionButton 
                      icon={ThumbsDown}
                      onClick={() => {}}
                      active={false}
                      title="Bad response"
                    />
                  </div>
                )}

              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
